/**
 * Pro plan pricing — single source of truth for monthly/annual USD tiers and Dodo product IDs.
 * Keep in sync with `functions/dodoPayments.js` PRO_TIERS / PRO_ANNUAL_TIERS / dodo-product-map.json.
 */

export interface ProPricingTier {
  subscribers: number;
  price: number;
}

export interface ProAnnualPricingTier {
  subscribers: number;
  /** Total billed per year (USD). */
  annualPrice: number;
}

/** Business plan — flat rate pricing (unlimited contacts, unlimited emails). */
export const BUSINESS_MONTHLY_PRICE = 249;
/** Business annual total: 10 months × $249 = 2 months free ($498 saved). */
export const BUSINESS_ANNUAL_PRICE = 2490;

export const PRO_MONTHLY_PRICING_TIERS: ProPricingTier[] = [
  { subscribers: 1000, price: 4.99 },
  { subscribers: 2500, price: 8.99 },
  { subscribers: 5000, price: 12.99 },
  { subscribers: 8000, price: 17.99 },
  { subscribers: 10000, price: 21.99 },
  { subscribers: 15000, price: 29.99 },
  { subscribers: 20000, price: 39.99 },
  { subscribers: 25000, price: 49.99 },
  { subscribers: 30000, price: 59.99 },
  { subscribers: 40000, price: 74.99 },
  { subscribers: 50000, price: 89.99 },
  { subscribers: 80000, price: 129.99 },
  { subscribers: 100000, price: 159.99 },
];

export const PRO_ANNUAL_PRICING_TIERS: ProAnnualPricingTier[] = [
  { subscribers: 1000, annualPrice: 49.9 },
  { subscribers: 2500, annualPrice: 89.9 },
  { subscribers: 5000, annualPrice: 129.9 },
  { subscribers: 8000, annualPrice: 179.9 },
  { subscribers: 10000, annualPrice: 219.9 },
  { subscribers: 15000, annualPrice: 299.9 },
  { subscribers: 20000, annualPrice: 399.9 },
  { subscribers: 25000, annualPrice: 499.9 },
  { subscribers: 30000, annualPrice: 599.9 },
  { subscribers: 40000, annualPrice: 749.9 },
  { subscribers: 50000, annualPrice: 899.9 },
  { subscribers: 80000, annualPrice: 1299.9 },
  { subscribers: 100000, annualPrice: 1599.9 },
];

/** Dodo product IDs: keys `pro_monthly_{cap}` / `pro_annual_{cap}`. */
export const DODO_PRO_PRODUCT_MAP: Record<string, string> = {
  pro_monthly_1000: 'pdt_0NdxAbspmqXbGYlfRO57V',
  pro_monthly_2500: 'pdt_0NdxAtFqMrvMqKabLKWtT',
  pro_monthly_5000: 'pdt_0NdxBD9W1lGytpG3Cqrxl',
  pro_monthly_8000: 'pdt_0NdxBR84ntehJpcb4zmF5',
  pro_monthly_10000: 'pdt_0NdxBz1T3WibcgjEQHbcW',
  pro_monthly_15000: 'pdt_0NdxCRyZlv8yTUMOtVlTd',
  pro_monthly_20000: 'pdt_0NdxCbnaE8CKdsx2zOFiz',
  pro_monthly_25000: 'pdt_0NdxCkr4IU8VTYdjeloo4',
  pro_monthly_30000: 'pdt_0NdxCzp6SQuDPm0ovRTHC',
  pro_monthly_40000: 'pdt_0NdxDD1ClarOBxYs4HW0h',
  pro_monthly_50000: 'pdt_0NdxDKuE7EMuTjvqQLPD6',
  pro_monthly_80000: 'pdt_0NdxDXHayHTcoS7w4nZf',
  pro_monthly_100000: 'pdt_0NdxDiNJNZC4rwv7zqPuO',
  pro_annual_1000: 'pdt_0NdxISRBdyzQ7QoST6Dv9',
  pro_annual_2500: 'pdt_0NdxQIGIDswpwzVtUL1YM',
  pro_annual_5000: 'pdt_0NdxQx4DqQpxAPEJgvfoR',
  pro_annual_8000: 'pdt_0NdxR7qavHfmBsQ6Nqlia',
  pro_annual_10000: 'pdt_0NdxRM4qlwVzDglIWRzG1',
  pro_annual_15000: 'pdt_0NdxRVSgXIFHHeslN1u8F',
  pro_annual_20000: 'pdt_0NdxRhMrgrp78DCyo99G8',
  pro_annual_25000: 'pdt_0NdxRquWKIQtPGkPKhckJ',
  pro_annual_30000: 'pdt_0NdxRzloDNdvj7WgLlTKg',
  pro_annual_40000: 'pdt_0NdxS8ISMJMIHksTk7JYE',
  pro_annual_50000: 'pdt_0NdxSFqPPAaspWAtXgYG1',
  pro_annual_80000: 'pdt_0NdxSMRNGrJjy0rzmCBZl',
  pro_annual_100000: 'pdt_0NdxSTuwP2suLEzCqUTpf',
};

function resolveTierCap(
  subscribers: number,
  caps: number[]
): number {
  if (subscribers < caps[0]) return caps[0];
  return caps.find((c) => subscribers <= c) ?? caps[caps.length - 1];
}

export function getProMonthlyPrice(
  subscribers: number,
  tiers: ProPricingTier[] = PRO_MONTHLY_PRICING_TIERS
): number {
  if (tiers.length === 0) return 0;
  if (subscribers < tiers[0].subscribers) return tiers[0].price;
  const tier = tiers.find((t) => subscribers <= t.subscribers) ?? tiers[tiers.length - 1];
  return tier.price;
}

export function getProAnnualPrice(
  subscribers: number,
  tiers: ProAnnualPricingTier[] = PRO_ANNUAL_PRICING_TIERS
): number {
  if (tiers.length === 0) return 0;
  if (subscribers < tiers[0].subscribers) return tiers[0].annualPrice;
  const tier = tiers.find((t) => subscribers <= t.subscribers) ?? tiers[tiers.length - 1];
  return tier.annualPrice;
}

/** Per-month figure shown on cards when annual billing is selected. */
export function getProEffectiveMonthlyDisplayPrice(
  subscribers: number,
  billingPeriod: 'monthly' | 'annual',
  monthlyTiers: ProPricingTier[] = PRO_MONTHLY_PRICING_TIERS,
  annualTiers: ProAnnualPricingTier[] = PRO_ANNUAL_PRICING_TIERS
): number {
  if (billingPeriod === 'monthly') {
    return getProMonthlyPrice(subscribers, monthlyTiers);
  }
  return getProAnnualPrice(subscribers, annualTiers) / 12;
}

/** Format USD plan amounts (supports cents). */
export function formatProPlanUsd(amount: number): string {
  if (!Number.isFinite(amount)) return '$0';
  const hasCents = Math.abs(amount % 1) > 0.001;
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  })}`;
}
